import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Hls from "hls.js";
import { motion } from "framer-motion";
import { Loader2, VideoOff } from "lucide-react";
import { liveLots as fallbackLots } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Illustrative "detections" the GeoDyn Vision overlay draws over the live feed (percent coords).
const BOXES = [
  { x: 12, y: 46, w: 9, h: 12 },
  { x: 24, y: 44, w: 9, h: 12 },
  { x: 36, y: 43, w: 9, h: 12 },
  { x: 49, y: 45, w: 9, h: 12 },
  { x: 62, y: 47, w: 9, h: 12 },
  { x: 74, y: 49, w: 9, h: 12 },
  { x: 18, y: 62, w: 10, h: 13 },
  { x: 33, y: 64, w: 10, h: 13 },
  { x: 55, y: 66, w: 10, h: 13 },
  { x: 71, y: 68, w: 10, h: 13 },
];

const Stat = ({ label, value, color, testid }) => (
  <div className="rounded-lg border border-white/10 bg-black/60 px-3 py-2 backdrop-blur-sm">
    <div className={`font-heading text-xl font-extrabold ${color}`} data-testid={testid}>
      {value.toLocaleString()}
    </div>
    <div className="font-mono text-[9px] uppercase tracking-wider text-white/50">{label}</div>
  </div>
);

const STATUS = {
  connecting: { label: "Connecting", dot: "bg-amber-400", ping: false },
  live: { label: "Live", dot: "bg-red-500", ping: true },
  paused: { label: "Live paused", dot: "bg-white/40", ping: false },
};

export const LiveDemand = () => {
  const [lots, setLots] = useState(fallbackLots);
  const [idx, setIdx] = useState(0);
  const [feed, setFeed] = useState("connecting"); // connecting | live | paused
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const lot = lots[idx] || fallbackLots[0];
  const [occ, setOcc] = useState(Math.round(lot.total * lot.baseOcc));

  // Resolve current live HLS urls from the backend.
  useEffect(() => {
    axios
      .get(`${API}/live/lots`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setLots(r.data);
      })
      .catch(() => {});
  }, []);

  // Auto-rotation between lots every 20s (reset when a user manually picks one).
  const startRotate = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % lots.length), 20000);
  }, [lots.length]);

  useEffect(() => {
    startRotate();
    return () => clearInterval(timerRef.current);
  }, [startRotate]);

  const pick = (i) => {
    setIdx(i);
    startRotate();
  };

  // Attach the live HLS stream to the <video> element.
  useEffect(() => {
    const video = videoRef.current;
    setFeed("connecting");
    if (!video || !lot.hlsUrl) {
      const t = setTimeout(() => setFeed("paused"), 1500);
      return () => clearTimeout(t);
    }

    let hls;
    const stall = setTimeout(() => setFeed((s) => (s === "live" ? s : "paused")), 12000);
    const onReady = () => {
      setFeed("live");
      video.play().catch(() => {});
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = lot.hlsUrl;
      video.addEventListener("loadeddata", onReady, { once: true });
    } else if (Hls.isSupported()) {
      hls = new Hls({ liveDurationInfinity: true, enableWorker: true, lowLatencyMode: true });
      hls.loadSource(lot.hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, onReady);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) setFeed("paused");
      });
    } else {
      setFeed("paused");
    }
    return () => {
      clearTimeout(stall);
      video.removeEventListener("loadeddata", onReady);
      if (hls) hls.destroy();
    };
  }, [lot.hlsUrl, idx]);

  // Simulated live occupancy read-out per lot (illustrative until on-prem vision is wired).
  useEffect(() => {
    const base = Math.round(lot.total * lot.baseOcc);
    setOcc(base);
    const t = setInterval(() => {
      const jitter = Math.round((Math.random() - 0.5) * 0.05 * lot.total);
      setOcc(Math.max(0, Math.min(lot.total, base + jitter)));
    }, 4000);
    return () => clearInterval(t);
  }, [idx, lot.total, lot.baseOcc]);

  const available = lot.total - occ;
  const pct = Math.round((occ / lot.total) * 100);
  const isLive = feed === "live";
  const st = STATUS[feed];

  return (
    <div data-testid="live-demand">
      <div className="relative h-[520px] overflow-hidden rounded-2xl border border-white/10 bg-[#060B14] shadow-[0_40px_120px_-40px_rgba(0,229,255,0.35)]">
        {/* branded backdrop (shown until the live pixels decode) */}
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(circle at 50% 40%, rgba(0,229,255,0.10) 0%, rgba(6,11,20,0) 60%)" }}
        />

        {/* real live feed */}
        <video
          ref={videoRef}
          muted
          autoPlay
          playsInline
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${isLive ? "opacity-100" : "opacity-0"}`}
          data-testid="live-video"
        />

        {/* GeoDyn Vision HUD overlay — only over a live picture */}
        {isLive && (
          <div className="pointer-events-none absolute inset-0">
            {BOXES.map((b, i) => (
              <motion.div
                key={i}
                className="absolute rounded-[3px] border border-brand-cyan/70"
                style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.25, 0.85, 0.25] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
              >
                <span className="absolute -top-4 left-0 whitespace-nowrap bg-brand-cyan px-1 font-mono text-[7px] font-bold uppercase tracking-wider text-black">
                  car {(0.9 + i * 0.008).toFixed(2)}
                </span>
                <span className="absolute -left-[1px] -top-[1px] h-1.5 w-1.5 border-l-2 border-t-2 border-brand-green" />
                <span className="absolute -bottom-[1px] -right-[1px] h-1.5 w-1.5 border-b-2 border-r-2 border-brand-green" />
              </motion.div>
            ))}
            <motion.div
              className="absolute inset-x-0 h-px bg-brand-cyan/70 shadow-[0_0_14px_3px_rgba(0,229,255,0.6)]"
              initial={{ top: "0%" }}
              animate={{ top: ["0%", "100%", "0%"] }}
              transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
            />
          </div>
        )}

        {/* connecting / paused center state */}
        {!isLive && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center" data-testid="live-placeholder">
            {feed === "connecting" ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-brand-cyan" />
                <div className="font-mono text-xs uppercase tracking-[0.28em] text-white/70">Connecting to live feed</div>
              </>
            ) : (
              <>
                <VideoOff className="h-8 w-8 text-white/50" />
                <div className="font-mono text-xs uppercase tracking-[0.28em] text-white/70">Live paused</div>
                <div className="max-w-[240px] text-xs text-white/40">
                  The live stream will resume shortly, or on a supported device.
                </div>
              </>
            )}
          </div>
        )}

        {/* top gradient + badges */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent" />
        <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-3 py-1.5 backdrop-blur-sm">
          <span className="relative flex h-2.5 w-2.5">
            {st.ping && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${st.dot} opacity-75`} />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${st.dot}`} />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-widest text-white" data-testid="live-status">
            {st.label}
          </span>
        </div>
        <div className="absolute right-4 top-4 rounded-full bg-brand-green/90 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-black">
          GeoDyn Vision
        </div>

        {/* bottom metrics panel */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-5">
          <div className="font-heading text-lg font-bold leading-tight text-white" data-testid="live-lot-name">
            {lot.name}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-white/50">{lot.city}</div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <Stat label="Total Spaces" value={lot.total} color="text-white" testid="live-total-spaces" />
            <Stat label="Occupied" value={occ} color="text-brand-cyan" testid="live-occupied" />
            <Stat label="Available" value={available} color="text-brand-green" testid="live-available" />
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-white/60">
              <span>AI Occupancy · demo analysis</span>
              <span>{pct}%</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/15">
              <div
                className="h-full bg-gradient-to-r from-brand-green to-brand-cyan transition-all duration-1000"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* lot picker */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="live-lot-picker">
        <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-white/40">Feeds</span>
        {lots.map((l, i) => (
          <button
            key={l.alias || i}
            data-testid={`live-lot-pick-${i}`}
            onClick={() => pick(i)}
            className={`rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${
              i === idx
                ? "border-brand-green bg-brand-green/15 text-brand-green"
                : "border-white/15 text-white/55 hover:border-white/30 hover:text-white/80"
            }`}
          >
            {l.pick || l.name}
          </button>
        ))}
      </div>
    </div>
  );
};
